import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Lock } from 'lucide-react';
import { formatDate, intlTag, type Locale } from '@castfeeds/i18n';
import { useT } from '@/i18n';
import { Empty, ErrorBox, Field, Ltr, QueryView, Spinner, useErrorText } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { buildUrl, sessionHeaders } from '@/lib/api/client';
import { useWs } from '@/lib/api/hooks';
import { qk } from '@/lib/api/keys';
import type { BillingInvoice, BillingProfile, BillingSummary, CheckoutStart } from '@/lib/api/types';
import { can } from '@/lib/constants';
import { logError, logInfo } from '@/lib/log';
import { external } from '@/lib/navigate';
import { useReadOnly } from '@/store/readonly';
import { useMembership, useSession } from '@/store/session';

export function money(locale: Locale, minor: number, currency: string): string {
  return new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency, minimumFractionDigits: minor % 100 === 0 ? 0 : 2 }).format(minor / 100);
}

const COUNTRIES = ['IL', 'US', 'GB', 'DE', 'FR', 'NL', 'ES', 'IT', 'CA', 'AU'];
const emptyProfile: BillingProfile = { legal_name: '', email: null, country: 'IL', currency: 'ILS', vat_id: null, address: null, city: null, postal_code: null };

/**
 * Billing (WS G · plan §11.1): billing details, the plan with upgrade/downgrade, the saved card, AI credit packs and
 * invoices. Every payment goes through the routed provider's hosted page; the page polls the transaction after the
 * customer returns, because the provider's webhook is what settles it.
 */
export function BillingPage() {
  const { t, locale } = useT();
  const w = useWs();
  const me = useMembership();
  const qc = useQueryClient();
  const errText = useErrorText();
  const owner = can(me?.role, 'billing.manage');
  const summary = useQuery({ queryKey: qk.billing(w), queryFn: api.billing.summary, enabled: owner });
  const invoices = useQuery({ queryKey: qk.billingInvoices(w), queryFn: api.billing.invoices, enabled: owner });
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error' | 'info'; text: string } | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: qk.billing(w) });

  // A payment lifts a billing suspension (amendment 15): once the summary says so, drop the shell's read-only banner.
  const suspended = summary.data?.suspended;
  useEffect(() => {
    if (!summary.data || suspended) return;
    const stale = useReadOnly.getState().code === 'workspace_suspended' || !!useSession.getState().memberships.find((m) => m.workspaceId === w)?.suspended;
    if (!stale) return;
    logInfo('billing', 'workspace is writable again; clearing the read-only banner');
    useReadOnly.getState().clear();
    useSession.setState((st) => ({ memberships: st.memberships.map((m) => (m.workspaceId === w ? { ...m, suspended: null } : m)) }));
    void qc.invalidateQueries({ queryKey: qk.me });
  }, [summary.data, suspended, w, qc]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      logError('billing', `${key} failed`, e);
      setNotice({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  };
  const goCheckout = (c: CheckoutStart) => {
    logInfo('billing', `checkout ${c.provider} tx=${c.transaction_id}`);
    setNotice({ tone: 'info', text: t('billRedirecting') });
    external.go(c.url);
  };

  if (!owner) {
    return (
      <div className="page" style={{ maxWidth: 760 }}>
        <h1 className="h1">{t('billTitle')}</h1>
        <div className="card card-pad"><span className="muted">{t('billOwnerOnly')}</span></div>
      </div>
    );
  }

  return (
    <div className="page stack" style={{ maxWidth: 980, gap: 18 }}>
      <div className="stack" style={{ gap: 4 }}>
        <h1 className="h1">{t('billTitle')}</h1>
        <span className="muted">{t('billSub')}</span>
      </div>
      <ReturnBanner onSettled={refresh} />
      {notice ? <div className={`alert alert-${notice.tone === 'error' ? 'error' : notice.tone === 'ok' ? 'ok' : 'info'}`} role={notice.tone === 'error' ? 'alert' : 'status'}>{notice.text}</div> : null}
      <QueryView query={summary}>
        {(s) => (
          <>
            {s.suspended?.billing ? <div className="alert alert-error" role="alert">{t('billSuspended')}</div> : null}
            <ProfileCard s={s} onSaved={() => { setNotice({ tone: 'ok', text: t('saved') }); void refresh(); }} />
            <PlanCard
              s={s}
              busy={busy}
              confirmCancel={confirmCancel}
              setConfirmCancel={setConfirmCancel}
              onCancel={() => run('cancel', async () => { await api.billing.cancel(); setConfirmCancel(false); await refresh(); })}
              onResume={() => run('resume', async () => { await api.billing.resume(); await refresh(); })}
              onUpdateCard={() => run('card', async () => goCheckout(await api.billing.updateCard(locale)))}
            />
            <PlansGrid
              s={s}
              busy={busy}
              onPick={(code) => run(`plan:${code}`, async () => {
                const live = s.subscription && s.subscription.status !== 'cancelled' && s.subscription.billing_model !== 'manual';
                if (!live) return goCheckout(await api.billing.checkout({ purpose: 'subscription', plan_code: code, locale }));
                const r = await api.billing.changePlan(code, locale);
                if (r.status === 'checkout' && r.checkout) return goCheckout(r.checkout);
                const currency = s.subscription?.currency ?? 'USD';
                setNotice({ tone: 'ok', text: r.status === 'scheduled' ? t('billScheduled') : r.charged_minor ? t('billUpgraded', { amount: money(locale, r.charged_minor, currency) }) : t('billChanged') });
                await refresh();
              })}
            />
            <CardsCard s={s} busy={busy} onUpdate={() => run('card', async () => goCheckout(await api.billing.updateCard(locale)))} />
            <PacksCard s={s} busy={busy} onBuy={(code) => run(`pack:${code}`, async () => goCheckout(await api.billing.checkout({ purpose: 'credit_pack', pack_code: code, locale })))} />
            <InvoicesCard q={invoices} />
            {s.provider ? (
              <p className="muted small row" style={{ gap: 6 }}>
                <Lock size={14} aria-hidden /> {t('billProcessedBy', { provider: s.provider.label })}
                {s.provider.merchant_of_record ? <> {t('billMerchantOfRecord', { provider: s.provider.label })}</> : null}
              </p>
            ) : s.profile ? <p className="muted small">{t('billManualOnly')}</p> : null}
          </>
        )}
      </QueryView>
    </div>
  );
}

/** After the hosted page sends the customer back: poll until the webhook has settled the transaction. */
function ReturnBanner({ onSettled }: { onSettled: () => void }) {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const outcome = params.get('checkout');
  const ref = params.get('ref');
  const [state, setState] = useState<{ status: string; error?: string | null } | null>(null);
  useEffect(() => {
    if (outcome !== 'success' || !ref) return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const tx = await api.billing.transaction(ref);
        if (stop) return;
        setState({ status: tx.status, error: tx.error_message });
        if (tx.status !== 'pending') {
          onSettled();
          return;
        }
      } catch {
        // the next attempt reports it
      }
      if (tries >= 30) setState({ status: 'slow' });
      else if (!stop) setTimeout(() => void tick(), 2000);
    };
    void tick();
    return () => {
      stop = true;
    };
  }, [outcome, ref, onSettled]);
  if (!outcome) return null;
  const dismiss = () => setParams((p) => { p.delete('checkout'); p.delete('ref'); return p; });
  let tone = 'info';
  let text = t('billReturnPending');
  if (outcome === 'cancelled') text = t('billReturnCancelled');
  else if (state?.status === 'succeeded') { tone = 'ok'; text = t('billReturnOk'); }
  else if (state?.status === 'failed' || state?.status === 'cancelled') { tone = 'error'; text = t('billReturnFailed', { reason: state.error ?? '—' }); }
  else if (state?.status === 'slow') text = t('billReturnSlow');
  return (
    <div className={`alert alert-${tone} row`} role="status" style={{ gap: 8 }}>
      {tone === 'info' && outcome === 'success' && state?.status !== 'slow' ? <Spinner size={14} /> : null}
      <span>{text}</span>
      <button type="button" className="btn btn-ghost btn-sm push" onClick={dismiss}>{t('close')}</button>
    </div>
  );
}

function ProfileCard({ s, onSaved }: { s: BillingSummary; onSaved: () => void }) {
  const { t } = useT();
  const errText = useErrorText();
  const [f, setF] = useState<BillingProfile>(s.profile ?? emptyProfile);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = !!s.subscription && s.subscription.status !== 'cancelled' && s.subscription.billing_model !== 'manual';
  const set = (p: Partial<BillingProfile>) => setF((x) => ({ ...x, ...p }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.billing.saveProfile({ ...f, email: f.email?.trim() || null, vat_id: f.vat_id?.trim() || null, address: f.address?.trim() || null, city: f.city?.trim() || null, postal_code: f.postal_code?.trim() || null });
      logInfo('billing', `profile saved ${f.country}/${f.currency}`);
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="card" aria-labelledby="bill-details">
      <div className="card-head"><h2 className="h2" id="bill-details">{t('billDetails')}</h2></div>
      <form className="card-pad stack" style={{ gap: 12 }} onSubmit={submit}>
        <span className="muted small">{t('billDetailsSub')}</span>
        {!s.profile ? <div className="alert alert-info">{t('billDetailsFirst')}</div> : null}
        <div className="grid-2">
          <Field label={t('billLegalName')}>{(p) => <input {...p} className="input" required minLength={2} value={f.legal_name} onChange={(e) => set({ legal_name: e.target.value })} />}</Field>
          <Field label={t('billEmail')}>{(p) => <input {...p} className="input" type="email" dir="ltr" value={f.email ?? ''} onChange={(e) => set({ email: e.target.value })} />}</Field>
          <Field label={t('billCountry')}>{(p) => (
            <>
              <input {...p} className="input" dir="ltr" list="bill-countries" maxLength={2} required value={f.country} onChange={(e) => set({ country: e.target.value.toUpperCase() })} />
              <datalist id="bill-countries">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist>
            </>
          )}</Field>
          <Field label={t('billCurrency')} hint={locked ? t('billCurrencyLocked') : undefined}>{(p) => (
            <select {...p} className="select" value={f.currency} disabled={locked} onChange={(e) => set({ currency: e.target.value })}>
              {s.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}</Field>
          <Field label={t('billVatId')} hint={f.country === 'IL' ? t('billVatHint') : undefined}>{(p) => <input {...p} className="input" dir="ltr" value={f.vat_id ?? ''} onChange={(e) => set({ vat_id: e.target.value })} />}</Field>
          <Field label={t('billAddress')}>{(p) => <input {...p} className="input" value={f.address ?? ''} onChange={(e) => set({ address: e.target.value })} />}</Field>
          <Field label={t('billCity')}>{(p) => <input {...p} className="input" value={f.city ?? ''} onChange={(e) => set({ city: e.target.value })} />}</Field>
          <Field label={t('billPostal')}>{(p) => <input {...p} className="input" dir="ltr" value={f.postal_code ?? ''} onChange={(e) => set({ postal_code: e.target.value })} />}</Field>
        </div>
        {error ? <div className="field-error" role="alert">{error}</div> : null}
        <div><button type="submit" className="btn btn-inv" disabled={saving || !/^[A-Z]{2}$/.test(f.country) || f.legal_name.trim().length < 2}>{saving ? <Spinner size={14} /> : null} {t('billSaveDetails')}</button></div>
      </form>
    </section>
  );
}

function PlanCard(p: { s: BillingSummary; busy: string | null; confirmCancel: boolean; setConfirmCancel: (v: boolean) => void; onCancel: () => void; onResume: () => void; onUpdateCard: () => void }) {
  const { t, locale } = useT();
  const sub = p.s.subscription;
  const d = (iso: string | null | undefined) => (iso ? formatDate(locale, iso) : '—');
  return (
    <section className="card" aria-labelledby="bill-plan">
      <div className="card-head"><h2 className="h2" id="bill-plan">{t('billCurrentPlan')}</h2></div>
      <div className="card-pad stack" style={{ gap: 10 }}>
        {!sub || sub.status === 'cancelled' ? <span className="muted">{t('billNoPlan')}</span> : (
          <>
            <div className="row wrap" style={{ gap: 10 }}>
              <strong style={{ fontSize: 18 }}>{sub.plan.name}</strong>
              <span className="pill" style={{ background: `var(--${sub.status === 'past_due' ? 'red' : sub.status === 'trialing' ? 'blue' : 'green'}-soft)` }}>{t(`billStatus_${sub.status}` as const)}</span>
              {sub.amount_minor !== null && sub.currency ? <span className="muted">{t(sub.plan.interval === 'year' ? 'billPerYear' : 'billPerMonth', { price: money(locale, sub.amount_minor, sub.currency) })}</span> : null}
            </div>
            {sub.status === 'trialing' ? <span>{t('billTrialEnds', { date: d(sub.trial_ends_at ?? sub.current_period_end) })}</span> : null}
            {sub.status === 'active' && !sub.cancel_at_period_end ? <span>{t('billRenews', { date: d(sub.current_period_end) })}</span> : null}
            {sub.cancel_at_period_end ? (
              <div className="row wrap" style={{ gap: 8 }}>
                <span>{t('billEndsOn', { date: d(sub.current_period_end) })}</span>
                {sub.billing_model !== 'provider_managed' ? <button type="button" className="btn btn-sm" disabled={!!p.busy} onClick={p.onResume}>{t('billResume')}</button> : null}
              </div>
            ) : null}
            {sub.pending_plan ? <span className="muted">{t('billPendingDowngrade', { plan: sub.pending_plan.name, date: d(sub.current_period_end) })}</span> : null}
            {sub.status === 'past_due' ? (
              <div className="alert alert-error row wrap" role="alert" style={{ gap: 8 }}>
                <span>{t('billPastDue')}</span>
                <button type="button" className="btn btn-sm btn-inv" disabled={!!p.busy} onClick={p.onUpdateCard}>{t('billUpdateCard')}</button>
              </div>
            ) : sub.dunning_attempts > 0 ? (
              <div className="alert alert-warn row wrap" role="status" style={{ gap: 8 }}>
                <span>{t('billRetrying', { n: sub.dunning_attempts + 1 })}</span>
                <button type="button" className="btn btn-sm" disabled={!!p.busy} onClick={p.onUpdateCard}>{t('billUpdateCard')}</button>
              </div>
            ) : null}
            {!sub.cancel_at_period_end && sub.billing_model !== 'manual' ? (
              p.confirmCancel ? (
                <div className="row wrap" style={{ gap: 8 }}>
                  <span>{t('billCancelConfirm', { date: d(sub.current_period_end) })}</span>
                  <button type="button" className="btn btn-sm btn-danger" disabled={!!p.busy} onClick={p.onCancel}>{p.busy === 'cancel' ? <Spinner size={12} /> : null} {t('billCancelYes')}</button>
                  <button type="button" className="btn btn-sm" onClick={() => p.setConfirmCancel(false)}>{t('billKeep')}</button>
                </div>
              ) : <div><button type="button" className="btn btn-ghost btn-sm" onClick={() => p.setConfirmCancel(true)}>{t('billCancel')}</button></div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function PlansGrid({ s, busy, onPick }: { s: BillingSummary; busy: string | null; onPick: (code: string) => void }) {
  const { t, locale } = useT();
  const sub = s.subscription && s.subscription.status !== 'cancelled' ? s.subscription : null;
  const currentPrice = s.plans.find((p) => p.current)?.price_minor ?? 0;
  return (
    <section className="card" aria-labelledby="bill-plans">
      <div className="card-head"><h2 className="h2" id="bill-plans">{t('billPlans')}</h2></div>
      <div className="card-pad grid-3" style={{ gap: 12 }}>
        {s.plans.map((p) => {
          const label = p.current ? t('billCurrent') : !sub || sub.billing_model === 'manual' ? (p.trial_days && !sub ? t('billStartTrial') : t('billChoose')) : (p.price_minor ?? 0) > currentPrice ? t('billUpgrade') : t('billDowngrade');
          return (
            <div key={p.code} className="card card-pad stack" style={{ gap: 6, borderColor: p.current ? 'var(--accent)' : undefined }}>
              <strong>{p.name}</strong>
              <span className="muted small">{p.description}</span>
              <span style={{ fontSize: 20, fontWeight: 700 }}>
                {p.price_minor !== null && p.currency ? t(p.interval === 'year' ? 'billPerYear' : 'billPerMonth', { price: money(locale, p.price_minor, p.currency) }) : <span className="muted small">{t('billNotSold', { currency: p.currency ?? '—' })}</span>}
              </span>
              <span className="small">{t('billIncluded', { credits: p.included_credits.toLocaleString(intlTag(locale)), storage: p.included_storage_gb >= 1024 ? `${p.included_storage_gb / 1024} TB` : `${p.included_storage_gb} GB` })}</span>
              {p.trial_days && !sub ? <span className="small muted">{t('billTrialDays', { days: p.trial_days })}</span> : null}
              <button
                type="button"
                className={`btn ${p.current ? '' : 'btn-inv'}`}
                disabled={p.current || p.price_minor === null || !s.profile || !!busy}
                onClick={() => onPick(p.code)}
                aria-label={`${label} · ${p.name}`}
              >
                {busy === `plan:${p.code}` ? <Spinner size={14} /> : null} {label}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CardsCard({ s, busy, onUpdate }: { s: BillingSummary; busy: string | null; onUpdate: () => void }) {
  const { t } = useT();
  const sub = s.subscription && s.subscription.status !== 'cancelled' ? s.subscription : null;
  if (!sub || sub.billing_model === 'manual') return null;
  return (
    <section className="card" aria-labelledby="bill-card">
      <div className="card-head">
        <h2 className="h2" id="bill-card">{t('billPaymentMethod')}</h2>
        <button type="button" className="btn btn-sm push" disabled={!!busy} onClick={onUpdate}>{busy === 'card' ? <Spinner size={12} /> : <CreditCard size={14} aria-hidden />} {t('billUpdateCard')}</button>
      </div>
      <div className="card-pad stack" style={{ gap: 6 }}>
        {sub.billing_model === 'provider_managed' ? <span className="muted">{t('billManagedBy', { provider: s.provider?.label ?? sub.provider })}</span> : s.payment_methods.length ? s.payment_methods.filter((m) => m.is_default).map((m) => (
          <span key={m.id} className="row" style={{ gap: 8 }}>
            <CreditCard size={16} aria-hidden />
            <Ltr>{m.brand ?? ''} •••• {m.last4 ?? ''}</Ltr>
            {m.exp_month ? <span className="muted small">{t('billCardExp', { date: `${String(m.exp_month).padStart(2, '0')}/${String(m.exp_year).slice(-2)}` })}</span> : null}
          </span>
        )) : <span className="muted">{t('billNoCard')}</span>}
      </div>
    </section>
  );
}

function PacksCard({ s, busy, onBuy }: { s: BillingSummary; busy: string | null; onBuy: (code: string) => void }) {
  const { t, locale } = useT();
  return (
    <section className="card" aria-labelledby="bill-credits">
      <div className="card-head">
        <h2 className="h2" id="bill-credits">{t('billCredits')}</h2>
        <span className="push muted">{t('billBalance', { credits: s.credit_balance.toLocaleString(intlTag(locale)) })}</span>
      </div>
      <div className="card-pad stack" style={{ gap: 10 }}>
        <span className="muted small">{t('billCreditsHint')}</span>
        {s.packs.length ? (
          <div className="grid-3" style={{ gap: 10 }}>
            {s.packs.map((p) => (
              <div key={p.code} className="card card-pad row" style={{ justifyContent: 'space-between', gap: 8 }}>
                <span className="stack" style={{ gap: 2 }}>
                  <strong>{p.name}</strong>
                  <span className="muted small">{p.price_minor !== null && p.currency ? money(locale, p.price_minor, p.currency) : t('billNotSold', { currency: p.currency ?? '—' })}</span>
                </span>
                <button type="button" className="btn btn-sm btn-inv" disabled={p.price_minor === null || !s.profile || !!busy} onClick={() => onBuy(p.code)} aria-label={`${t('billBuy')} · ${p.name}`}>
                  {busy === `pack:${p.code}` ? <Spinner size={12} /> : null} {t('billBuy')}
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function InvoicesCard({ q }: { q: { data: BillingInvoice[] | undefined; isLoading: boolean; error: unknown; refetch: () => unknown } }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const [err, setErr] = useState<string | null>(null);
  const open = async (inv: BillingInvoice) => {
    setErr(null);
    try {
      const res = await fetch(buildUrl(api.billing.documentPath(inv.id)), { headers: sessionHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (res.headers.get('content-type')?.includes('application/json')) external.go(((await res.json()) as { url: string }).url);
      else window.open(URL.createObjectURL(await res.blob()), '_blank', 'noopener');
    } catch (e) {
      logError('billing', `document ${inv.number}`, e);
      setErr(errText(e));
    }
  };
  return (
    <section className="card" aria-labelledby="bill-invoices">
      <div className="card-head"><h2 className="h2" id="bill-invoices">{t('billInvoices')}</h2></div>
      {q.error ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : q.isLoading ? <div className="card-pad"><Spinner /></div> : !q.data?.length ? <Empty>{t('billNoInvoices')}</Empty> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{t('billNumber')}</th><th>{t('billDate')}</th><th className="num">{t('billAmount')}</th><th>{t('billCurrentPlan')}</th><th /></tr></thead>
            <tbody>
              {q.data.map((i) => (
                <tr key={i.id}>
                  <td><Ltr className="mono">{i.number}</Ltr><div className="small muted">{i.description}</div></td>
                  <td>{formatDate(locale, i.paid_at ?? i.issued_at)}</td>
                  <td className="num">{money(locale, i.amount_minor, i.currency)}{i.refunded_minor ? <div className="small muted">{t('billRefunded', { amount: money(locale, i.refunded_minor, i.currency) })}</div> : null}</td>
                  <td>{t(`billInvStatus_${i.status}` as const)}</td>
                  <td>
                    {i.document.status === 'pending' || i.document.status === 'failed' ? <span className="small muted">{t('billDocPending')}</span>
                      : i.document.available ? <button type="button" className="btn btn-sm" onClick={() => void open(i)}>{t('billDownload')}</button> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {err ? <div className="card-pad field-error" role="alert">{err}</div> : null}
    </section>
  );
}
